import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { PlanPeriodSummary } from 'core/application/employeeDashboard/summary/benefitSummary/getSummary'

import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Modal from 'common/components/feedback/Modal'
import Button from 'common/components/general/Button'

import { parseZoneFormat } from 'common/dateFormatters'

type ContributeToHsaModalProps = {
  visible: boolean
  loading?: boolean
  planPeriodSummary: PlanPeriodSummary
  onContributeToHsaChanged?: (value) => void
  onSubmitForm?: (contributesToHsa: boolean) => void
  onCancel?: () => void
  onBack?: () => void
}
const ContributeToHsaModal: React.FC<ContributeToHsaModalProps> = (props) => {
  const [initialValues] = useState<{ contributeToHsa?: boolean }>({})
  const [isValid, setIsValid] = useState<boolean>(false)
  const [formValues, setFormValues] = useState<{ contributeToHsa?: boolean }>()
  const { t: translate } = useTranslation()

  return (
    <Modal
      visible={props.visible}
      onCancel={() => props.onCancel()}
      title="Contributing to an HSA?"
      closable={true}
      footer={
        <>
          <Button type="default" onClick={() => props.onBack()}>
            Back
          </Button>

          <Button
            type="primary"
            disabled={!isValid}
            loading={props.loading}
            onClick={() => {
              if (props.onSubmitForm) {
                props.onSubmitForm(formValues.contributeToHsa)
              }
            }}
          >
            Next
          </Button>
        </>
      }
    >
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={initialValues}
        onSubmit={() => {}}
        validate={(values) => {
          const isValid: boolean = formValidator(translate).isValidSync(values)
          setIsValid(isValid)
          setFormValues(values)
          if (isValid && props.onContributeToHsaChanged) {
            props.onContributeToHsaChanged(values.contributeToHsa)
          }
        }}
      >
        {(formik) => {
          return (
            <form autoComplete="off">
              <div className={'mb-16'}>
                Are you, your spouse, or an employer contributing to a health savings account (HSA) during the{' '}
                <span>
                  {parseZoneFormat(props.planPeriodSummary.startDate)} -{' '}
                  {parseZoneFormat(props.planPeriodSummary.endDate)}{' '}
                </span>
                plan year?
              </div>

              <Row>
                <Col span={12} className="mt-8">
                  <Radio.Group name={'contributeToHsa'}>
                    <Row gutter={5} className={'mb-16'}>
                      <Col span={12}>
                        <Radio.Button key={'1'} size="large" value={false}>
                          No
                        </Radio.Button>
                      </Col>
                      <Col span={12}>
                        <Radio.Button key={'2'} size="large" value={true}>
                          Yes
                        </Radio.Button>
                      </Col>
                    </Row>
                  </Radio.Group>
                </Col>
              </Row>

              {formik?.values.contributeToHsa != undefined && !formik?.values.contributeToHsa && (
                <div>
                  OK, this means you can use your PeopleKeep health benefit to get reimbursed for most medical expenses,
                  including insurance deductibles, copays for providers, and prescriptions, etc.
                </div>
              )}

              {formik?.values.contributeToHsa != undefined && formik?.values.contributeToHsa && (
                <div>
                  That’s great. HSAs can help you qualify for extra tax benefits, but they also come with extra rules
                  when used in conjunction with your PeopleKeep health benefit.
                </div>
              )}
            </form>
          )
        }}
      </Formik>
    </Modal>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    contributeToHsa: Yup.boolean().required(translate('general.required')).nullable(),
  })

export default ContributeToHsaModal
